<template>
  <div>
    <div class="row">
      <div class="col-6">
        <div class="z-card mb-4">
          <h2>{{ $t('autocaller_v2.campaign.form.title') }}</h2>
          <div class="z-card__body">
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.name') }}
              </div>
              <z-form-input class="col-9" :rules="{ required: true, max: 32 }">
                <i-input v-model="form.name" />
              </z-form-input>
            </z-form-group>
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.instance') }}
              </div>
              <z-form-input class="col-9" :rules="{ required: true }">
                <select-region-instanse
                  :id="campaign.instance_id"
                  v-model="form.instance_id"
                  :disabled="disabled"
                />
              </z-form-input>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.welcome_message') }}
              </div>
              <tooltip
                class="ml-3"
                min-width="300"
                max-width="300"
                placement="top"
                theme="light"
                :content="
                  disabled
                    ? $t('autocaller_v2.campaign.tooltips.welcome_message_with_instance')
                    : $t('autocaller_v2.campaign.tooltips.welcome_message_with_select_instance')
                "
              >
                <i-button
                  class="file-upload-button"
                  :disabled="!form.instance_id"
                  @click="openUploadModal"
                >
                  {{
                    form.welcome_message_file_id
                      ? welcome_message_title
                      : $tc('pbx.worksheet.action.menu.input.select_file', 0)
                  }}

                  <icon class="ml-2" type="ios-cloud-upload" size="20" />
                </i-button>
              </tooltip>
              <tooltip
                class="ml-3"
                min-width="300"
                max-width="300"
                placement="top"
                theme="light"
                :content="$t('autocaller_v2.campaign.tooltips.welcome_message')"
              >
                <icon size="18" type="ios-help-circle-outline" />
              </tooltip>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.webhook.field.type') }}
              </div>
              <div class="col-9">
                <z-form-input>
                  <select-type v-model="form.type" :clearable="false" :disabled="disabled" />
                </z-form-input>
              </div>
            </z-form-group>
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.caller_id') }}
              </div>
              <z-form-input class="col-9" :rules="{ required: true, min: 9, max: 18 }">
                <select-caller-id v-model="form.callers" />
              </z-form-input>
            </z-form-group>
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.weight') }}
              </div>
              <z-form-input class="col-9 flex flex--baseline">
                <input-number v-model="form.weight" :min="0" :max="100" @on-blur="defaultWeight" />
                <tooltip
                  class="ml-3"
                  placement="top"
                  min-width="200"
                  max-width="200"
                  theme="light"
                  :content="$t('autocaller_v2.campaign.tooltips.weight')"
                >
                  <icon size="18" type="ios-help-circle-outline" />
                </tooltip>
              </z-form-input>
            </z-form-group>
          </div>
        </div>
      </div>
      <div class="col-6">
        <div class="z-card mb-4">
          <h2>{{ $t('autocaller_v2.campaign.form.parameters') }}</h2>
          <div class="z-card__body">
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.max_attempts') }}
              </div>
              <z-form-input class="col-9">
                <input-number
                  v-model="form.attempts_interval_min"
                  :min="1"
                  :max="12"
                  @input="checkStrategy"
                  @on-blur="attemptsIntervalMin"
                />
              </z-form-input>
            </z-form-group>

            <z-form-group v-if="form.attempts_interval_min >= 2" class="row">
              <z-form-input class="col-9 offset-3">
                <select-number-attemps
                  :times="form.attempts_interval_min"
                  :strategy="form.strategy"
                  @input="addStrategy"
                />
              </z-form-input>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.max_time') }}
              </div>
              <z-form-input class="col-9 flex flex--baseline">
                <input-number
                  v-model="form.dial_timeout"
                  :min="1"
                  :max="9999"
                  @on-blur="defaultMaxTime"
                />
                <tooltip
                  class="ml-3"
                  placement="top"
                  min-width="200"
                  max-width="200"
                  theme="light"
                  :content="$t('autocaller_v2.campaign.tooltips.max_time')"
                >
                  <icon size="18" type="ios-help-circle-outline" />
                </tooltip>
              </z-form-input>
            </z-form-group>

            <template v-if="form.type == 'predictive'">
              <z-form-group class="row">
                <div class="col-3 text-right">
                  {{ $t('autocaller_v2.campaign.field.avg_task') }}
                </div>
                <z-form-input class="col-9 flex flex--baseline">
                  <input-number
                    v-model="form.average_task_duration"
                    :min="1"
                    :max="9999"
                    @on-blur="averageTime"
                  />
                  <tooltip
                    class="ml-3"
                    placement="top"
                    min-width="200"
                    max-width="200"
                    theme="light"
                    :content="$t('autocaller_v2.campaign.tooltips.avg_task')"
                  >
                    <icon size="18" type="ios-help-circle-outline" />
                  </tooltip>
                </z-form-input>
              </z-form-group>
              <z-form-group class="row">
                <div class="col-3 text-right">
                  {{ $t('autocaller_v2.campaign.field.dt_calls') }}
                </div>
                <z-form-input class="col-9 flex flex--baseline">
                  <input-number
                    v-model="form.dt_affecting_calls"
                    :min="1"
                    :max="9999"
                    @on-blur="dtAffectingCalls"
                  />
                  <tooltip
                    class="ml-3"
                    placement="top"
                    min-width="200"
                    max-width="200"
                    theme="light"
                    :content="$t('autocaller_v2.campaign.tooltips.dt_calls')"
                  >
                    <icon size="18" type="ios-help-circle-outline" />
                  </tooltip>
                </z-form-input>
              </z-form-group>
            </template>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.record') }}
              </div>
              <z-form-input class="col-9">
                <z-switch v-model="form.record_calls_switch" />
              </z-form-input>
            </z-form-group>
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.status') }}
              </div>
              <z-form-input class="col-9">
                <z-switch v-model="form.active_switch" />
              </z-form-input>
            </z-form-group>
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.skills') }}
              </div>
              <z-form-input class="col-9">
                <z-switch v-model="form.skills" />
              </z-form-input>
            </z-form-group>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-6">
        <div class="z-card mb-4">
          <h2>{{ $t('autocaller_v2.campaign.form.settings') }}</h2>
          <div class="z-card__body">
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.asr_default') }}
              </div>
              <z-form-input class="col-9">
                <slider v-model="form.default_asr" :tip-format="getPercentFormat" />
              </z-form-input>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.asr') }}
              </div>
              <z-form-input class="col-9">
                <z-switch v-model="asr_switch" />
              </z-form-input>
            </z-form-group>
            <z-form-group v-if="asr_switch" class="row">
              <z-form-input class="offset-3 col-9">
                <slider v-model="form.asr" />
              </z-form-input>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.resetAsr') }}
              </div>
              <z-form-input class="col-9">
                <z-switch v-model="form.asr_reset" />
              </z-form-input>
            </z-form-group>
            <template v-if="form.asr_reset">
              <div :class="{ reset_asr: asr_switch }">
                <z-form-group class="row">
                  <div class="col-3 text-right">
                    {{ $t('autocaller_v2.campaign.field.lost_calls') }}
                  </div>
                  <z-form-input class="col-9">
                    <input-number
                      v-model="form.bounce_calc_period"
                      :min="1"
                      :max="9999"
                      @on-blur="defaultCalcPeriod"
                    />
                  </z-form-input>
                </z-form-group>
                <z-form-group class="row">
                  <div class="col-3 text-right">
                    {{ $t('autocaller_v2.campaign.field.percent_lost_calls') }}
                  </div>
                  <z-form-input class="col-9">
                    {{ form.bounce_drop_threshold }}
                    <slider v-model="form.bounce_drop_threshold" :tip-format="getPercentFormat" />
                  </z-form-input>
                </z-form-group>
                <z-form-group class="row">
                  <div class="col-3 text-right">
                    {{ $t('autocaller_v2.campaign.field.wait_operator') }}
                  </div>
                  <z-form-input class="col-9">
                    <input-number
                      v-model="form.bounce_duration_threshold"
                      :min="1"
                      :max="9999"
                      @on-blur="defaultDurationThreshold"
                    />
                  </z-form-input>
                </z-form-group>
              </div>
            </template>

            <z-form-group class="row" style="align-items: flex-start">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.sort') }}
              </div>
              <div class="col-8">
                <z-form-input>
                  <campaign-sort :order_rules="form.order_rules" @sort="sort" />
                </z-form-input>
              </div>
            </z-form-group>
          </div>
        </div>
      </div>
      <div class="col-6">
        <div class="z-card">
          <div class="ml-0 row vertical-center">
            <h2 class="mr-2">{{ $t('autocaller_v2.campaign.form.determinant') }}</h2>
            <z-switch v-model="form.amd" />
          </div>
          <div v-if="form.amd" class="z-card__body">
            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.type') }}
              </div>
              <z-form-input class="col-8" :rules="{ required: true }">
                <select-machine-detection-type
                  :id="form.amd_async"
                  v-model="form.amd_async"
                  :disabled="is_block_disabled"
                />
              </z-form-input>
              <tooltip
                class="ml-3"
                placement="top"
                min-width="350"
                max-width="350"
                theme="light"
                :content="
                  form.amd_async === 0
                    ? $t('autocaller_v2.campaign.tooltips.sync_type')
                    : $t('autocaller_v2.campaign.tooltips.async_type')
                "
              >
                <icon size="18" type="ios-help-circle-outline" />
              </tooltip>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.machine_detection.duration') }}
              </div>
              <z-form-input class="col-8">
                <slider v-model="form.amd_duration" :min="3" :max="10" />
              </z-form-input>
              <tooltip
                class="ml-3"
                placement="top"
                min-width="350"
                max-width="350"
                theme="light"
                :content="$t('autocaller_v2.campaign.tooltips.duration')"
              >
                <icon size="18" type="ios-help-circle-outline" />
              </tooltip>
            </z-form-group>

            <z-form-group class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.machine_detection.on_threshold') }}
              </div>
              <z-form-input class="col-8" :rules="{ required: true }">
                <z-switch v-model="form.amd_drop_on_threshold" :disabled="is_block_disabled" />
              </z-form-input>
              <tooltip
                class="ml-3"
                placement="top"
                min-width="350"
                max-width="350"
                theme="light"
                :content="$t('autocaller_v2.campaign.tooltips.drop_call')"
              >
                <icon size="18" type="ios-help-circle-outline" />
              </tooltip>
            </z-form-group>

            <z-form-group v-if="form.amd_drop_on_threshold" class="row">
              <div class="col-3 text-right">
                {{ $t('autocaller_v2.campaign.field.machine_detection.threshold') }}
              </div>
              <z-form-input class="col-8">
                <slider v-model="form.amd_drop_threshold" :tip-format="getPercentFormat" />
              </z-form-input>
              <tooltip
                class="ml-3"
                placement="top"
                min-width="350"
                max-width="350"
                theme="light"
                :content="$t('autocaller_v2.campaign.tooltips.threshold')"
              >
                <icon size="18" type="ios-help-circle-outline" />
              </tooltip>
            </z-form-group>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import SelectCallerId from './campaigns-input-caller-id';
  import SelectType from './campaigns-filter-type';
  import SelectRegionInstanse from './campaigns-input-region-instance';
  import SelectNumberAttemps from './campaigns-input-number-attempts';
  import CampaignSort from './campaigns-sort';
  import SelectMachineDetectionType from './selects/type';
  import { CampaignsModalService } from '@autocaller_v2/modals/CampaignsModalService';
  import { PredictiveMediaService } from '@autocaller_v2/services/PredictiveMediaService';
  import { mapActions } from 'vuex';

  export default {
    components: {
      SelectCallerId,
      SelectRegionInstanse,
      SelectType,
      SelectNumberAttemps,
      CampaignSort,
      SelectMachineDetectionType
    },
    props: {
      campaign: {
        type: [Object, Boolean],
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      files: [],
      form: {
        name: '',
        instance_id: '',
        callers: [],
        operator_id: '',
        strategy: [],
        active_switch: false,
        active: 0,
        type: 'progressive',
        weight: 0,
        welcome_message_file_id: null,
        dial_timeout: 30,
        average_task_duration: 60,
        dt_affecting_calls: 10,
        asr: 0,
        asr_reset: false,
        default_asr: 100,
        bounce_calc_period: 60,
        bounce_drop_threshold: 5,
        bounce_duration_threshold: 1,
        record_calls_switch: false,
        record_calls: false,
        order_rules: [],
        amd: false,
        amd_async: 0,
        amd_duration: 7,
        amd_drop_on_threshold: false,
        amd_drop_threshold: 70,
        skills: false
      },
      asr_switch: false,
      is_block_disabled: false,
      welcome_message_title: ''
    }),
    watch: {
      'form.instance_id': {
        handler: function (new_id) {
          this.getMediaByInstanceId(new_id);

          if (this.form.instance_id !== new_id) {
            this.form.welcome_message_file_id = null;
          }
        }
      },
      form: {
        immediate: true,
        handler(value) {
          this.$emit('input', value);
        }
      },
      asr_switch: {
        immediate: true,
        handler() {
          if (!this.asr_switch) this.form.asr = 0;
        }
      }
    },
    created() {
      if (this.campaign) {
        const {
          name,
          active,
          instance_id,
          welcome_message_file_id,
          callers,
          operator_id,
          type,
          weight,
          dial_timeout,
          average_task_duration,
          dt_affecting_calls,
          strategy,
          asr,
          bounce_calc_period,
          bounce_drop_threshold,
          bounce_duration_threshold,
          record_calls,
          order_rules,
          default_asr,
          asr_start,
          amd,
          amd_async,
          amd_duration,
          amd_drop_on_threshold,
          amd_drop_threshold,
          skills
        } = this.campaign;
        this.form = {
          name,
          active_switch: Boolean(active),
          instance_id,
          welcome_message_file_id,
          attempts_interval_min: strategy?.length > 0 ? strategy?.length + 1 : 1,
          asr_reset: Boolean(bounce_duration_threshold),
          callers,
          operator_id,
          type,
          weight,
          dial_timeout,
          average_task_duration,
          dt_affecting_calls,
          strategy: strategy ? strategy : [],
          asr,
          asr_start,
          bounce_calc_period,
          bounce_drop_threshold,
          bounce_duration_threshold,
          record_calls_switch: Boolean(record_calls),
          order_rules,
          default_asr,
          amd,
          amd_async: amd_async ? 1 : 0,
          amd_duration,
          amd_drop_on_threshold,
          amd_drop_threshold,
          skills
        };

        this.asr_switch = Boolean(asr);
      }
    },
    methods: {
      ...mapActions('autocaller_v2/campaigns/table', ['setMediafiles']),

      getMediaByInstanceId(instance_id) {
        PredictiveMediaService.getList(instance_id).then((data) => {
          this.setMediafiles(data);
          const selectedFile = data?.find((i) => i.id === this.form.welcome_message_file_id);

          if (selectedFile?.id) {
            this.welcome_message_title = `${selectedFile.description}${
              selectedFile.extension ? '.' + selectedFile.extension : ''
            }`;
          }
        });
      },
      openUploadModal() {
        this.getMediaByInstanceId(this.form.instance_id);

        CampaignsModalService.open_upload(
          this.form.instance_id,
          this.form.welcome_message_file_id
        ).on('success', (selectedFile) => {
          this.welcome_message_title = `${selectedFile?.description}${
            selectedFile?.extension ? '.' + selectedFile?.extension : ''
          }`;

          this.form.welcome_message_file_id = selectedFile?.id || null;
        });
      },
      getPercentFormat(val) {
        return `${val}%`;
      },
      checkStrategy(i) {
        if (!this.form.attempts_interval_min) {
          this.form.strategy = [];
          return false;
        }
        if (this.form.strategy?.length > i - 1) {
          this.form.strategy.length = this.form.attempts_interval_min - 1;
          return false;
        }
        this.form.strategy[i - 2] = 1;
      },
      defaultWeight() {
        if (this.form.weight == null) this.form.weight = 0;
      },
      defaultMaxTime() {
        if (!this.form.dial_timeout) this.form.dial_timeout = 30;
      },
      averageTime() {
        if (!this.form.average_task_duration) this.form.average_task_duration = 60;
      },
      dtAffectingCalls() {
        if (!this.form.dt_affecting_calls) this.form.dt_affecting_calls = 10;
      },
      attemptsIntervalMin() {
        if (!this.form.attempts_interval_min) this.form.attempts_interval_min = 1;
      },
      defaultCalcPeriod() {
        if (!this.form.bounce_calc_period) this.form.bounce_calc_period = 60;
      },
      defaultDurationThreshold() {
        if (!this.form.bounce_duration_threshold) this.form.bounce_duration_threshold = 1;
      },
      sort(e) {
        this.form.order_rules = e;
      },
      addStrategy(val, i) {
        this.form.strategy[i - 1] = val;
      },
      switchStatus(val) {
        this.asr = val;
      },
      switchStatusReset(val) {
        this.resetAsr = val;
      }
    }
  };
</script>
